import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, ChevronUp, ChevronDown, X, ImagePlus, Loader2 } from 'lucide-react';
import PhotoUpload from './PhotoUpload';
import BulkUploadButton from './BulkUploadButton';
import { addRowsFromUrls } from '@/components/editable/PhotoRowsInline';

/**
 * GalleryRowsEditor
 * -----------------
 * Row-based gallery editor with mixed-layout rows.
 * Each row has { layout, images: [url, url, …] } and its own order.
 *
 * Layouts:
 *   - "3-equal"    : 3 equal portraits         (3 images)
 *   - "big-left-2" : 1 big portrait + 2 stacked (3 images)
 *   - "2-big-right": 2 stacked + 1 big         (3 images)
 *   - "2-equal"    : 2 equal portraits         (2 images)
 *   - "solo"       : single hero shot          (1 image)
 *   - "4-equal"    : 4 equal columns           (4 images)
 */

export const LAYOUTS = [
  { id: '3-equal', label: '3 equal portraits', slots: 3, cols: 'grid-cols-1 md:grid-cols-3' },
  { id: 'big-left-2', label: '1 big left + 2 small right', slots: 3, cols: 'grid-cols-1 md:grid-cols-3' },
  { id: '2-big-right', label: '2 small left + 1 big right', slots: 3, cols: 'grid-cols-1 md:grid-cols-3' },
  { id: '2-equal', label: '2 equal portraits', slots: 2, cols: 'grid-cols-1 md:grid-cols-2' },
  { id: 'solo', label: 'Single hero shot', slots: 1, cols: 'grid-cols-1' },
  { id: '4-equal', label: '4 equal columns', slots: 4, cols: 'grid-cols-2 md:grid-cols-4' },
];

const layoutBy = (id) => LAYOUTS.find(l => l.id === id) || LAYOUTS[0];

export default function GalleryRowsEditor({ scope = 'main', title = 'Main Site Gallery' }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/site/gallery-rows', { params: { scope } });
      setRows((data || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    } catch { toast.error('Failed to load rows'); }
  };
  useEffect(() => { load(); }, [scope]);

  const addRow = async () => {
    setBusy(true);
    try {
      const order = rows.length ? Math.max(...rows.map(r => r.order ?? 0)) + 1 : 0;
      await api.post('/site/gallery-rows', { scope, layout: '3-equal', images: ['', '', ''], order });
      toast.success('Row added');
      await load();
    } catch { toast.error('Add failed'); } finally { setBusy(false); }
  };

  const patchRow = async (id, patch) => {
    setBusy(true);
    try { await api.patch(`/site/gallery-rows/${id}`, patch); await load(); }
    catch { toast.error('Save failed'); } finally { setBusy(false); }
  };

  const removeRow = async (id) => {
    if (!window.confirm('Delete this row?')) return;
    await api.delete(`/site/gallery-rows/${id}`);
    toast.success('Deleted');
    await load();
  };

  const moveRow = async (id, delta) => {
    const idx = rows.findIndex(r => r.id === id);
    const swap = idx + delta;
    if (swap < 0 || swap >= rows.length) return;
    // Normalise every row's order to defeat ties, then swap the target pair.
    const reindexed = rows.map((r, i) => ({ id: r.id, order: i }));
    [reindexed[idx].order, reindexed[swap].order] = [reindexed[swap].order, reindexed[idx].order];
    await Promise.all(reindexed.map(r => api.patch(`/site/gallery-rows/${r.id}`, { order: r.order })));
    await load();
  };

  const setSlot = async (row, slotIdx, url) => {
    const nextImgs = [...(row.images || [])];
    while (nextImgs.length <= slotIdx) nextImgs.push('');
    nextImgs[slotIdx] = url;
    await patchRow(row.id, { images: nextImgs });
  };

  const changeLayout = async (row, newLayout) => {
    const lo = layoutBy(newLayout);
    const nextImgs = [...(row.images || [])];
    while (nextImgs.length < lo.slots) nextImgs.push('');
    nextImgs.length = lo.slots;
    await patchRow(row.id, { layout: newLayout, images: nextImgs });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <div>
          <h3 className="font-serif text-xl text-royal font-bold">{title} <span className="text-sm text-charcoal-muted font-normal">({rows.length} rows)</span></h3>
          <p className="text-xs text-charcoal-muted mt-1">Each row can use a different mixed-portrait layout. Reorder with ▲▼, drop photos into any slot, or delete rows entirely.</p>
        </div>
        <div className="flex items-center gap-2">
          <BulkUploadButton testid={`gallery-rows-bulk-${scope}`}
            onDone={async (urls) => { await addRowsFromUrls(scope, urls, rows.length ? Math.max(...rows.map(r => r.order ?? 0)) + 1 : 0); await load(); }} />
          <button onClick={addRow} disabled={busy} data-testid={`gallery-rows-add-${scope}`}
            className="btn-royal text-sm py-2 px-4 flex items-center gap-1">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Row
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {rows.length === 0 && (
          <div className="bg-white border rounded-lg p-8 text-center text-sm text-charcoal-muted">
            No rows yet. Click <strong>Add Row</strong> to build your first mixed-portrait layout.
          </div>
        )}
        {rows.map((r, i) => {
          const lo = layoutBy(r.layout);
          return (
            <div key={r.id} data-testid={`gallery-row-${r.id}`} className="bg-white border rounded-lg p-4">
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <div className="text-xs uppercase tracking-widest text-gold font-bold">Row {i + 1}</div>
                <select value={r.layout || '3-equal'} onChange={(e) => changeLayout(r, e.target.value)}
                  data-testid={`gallery-row-layout-${r.id}`}
                  className="text-xs px-2 py-1 border rounded">
                  {LAYOUTS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
                </select>
                <div className="ml-auto flex items-center gap-1">
                  <button onClick={() => moveRow(r.id, -1)} disabled={i === 0 || busy} title="Move up" className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                  <button onClick={() => moveRow(r.id, 1)} disabled={i === rows.length - 1 || busy} title="Move down" className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                  <button onClick={() => removeRow(r.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Delete row"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className={`grid ${lo.cols} gap-3`}>
                {Array.from({ length: lo.slots }).map((_, slotIdx) => (
                  <RowSlot key={slotIdx} row={r} slotIdx={slotIdx} onChange={setSlot} layout={lo.id} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RowSlot({ row, slotIdx, onChange, layout }) {
  const url = (row.images || [])[slotIdx] || '';
  // Highlight the "big" slot so admins see the intended proportions.
  const isBig = (layout === 'big-left-2' && slotIdx === 0) || (layout === '2-big-right' && slotIdx === 2);
  const isSmallStack = (layout === 'big-left-2' && slotIdx > 0) || (layout === '2-big-right' && slotIdx < 2);
  const aspect = isBig ? 'aspect-[3/4] md:aspect-[4/5]' : isSmallStack ? 'aspect-[4/3]' : 'aspect-[3/4]';
  const span = isBig ? 'md:row-span-2' : '';
  return (
    <div className={`${span}`}>
      <div className="text-[10px] uppercase tracking-widest text-charcoal-muted mb-1">
        Slot {slotIdx + 1} {isBig && '· big'}
      </div>
      <div className={`${aspect} bg-royal/5 border-2 border-dashed border-gold/40 rounded overflow-hidden relative group`}>
        {url ? (
          <>
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button
              onClick={() => onChange(row, slotIdx, '')}
              className="absolute top-1 right-1 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
              title="Remove image"
            ><X className="w-3.5 h-3.5 text-red-600" /></button>
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-charcoal-muted text-xs">
            <ImagePlus className="w-6 h-6 mb-1" />
            <span>Drop or upload</span>
          </div>
        )}
      </div>
      <div className="mt-2">
        <PhotoUpload label="" value={url} onChange={(v) => onChange(row, slotIdx, v)} category="gallery" />
      </div>
    </div>
  );
}
